import { Controller } from '@hotwired/stimulus'
import { post } from '../lib/request'

// Recent pages, so support can see what someone was looking at. Stored per
// subdomain (localStorage is per origin) and only sent with a message.
const HISTORY_KEY = 'darkroomSupportHistory'
const HISTORY_LIMIT = 20
const HISTORY_MAX_AGE = 24 * 60 * 60 * 1000
// Paths with a secret in them. Query strings and hashes are always dropped
// (e.g. confirm?token=). SupportUrl in the app does the same as a backstop.
const SECRET_PATHS = [/^(\/recover_password\/)[^/]+/]

// never let a token out of the browser: no query, no hash, secrets masked
const scrubUrl = (value) => {
  try {
    const url = new URL(value)
    const path = SECRET_PATHS.reduce((p, regex) => p.replace(regex, '$1[token]'), url.pathname)
    return `${url.origin}${path}`
  }
  catch {
    return ''
  }
}

export default class extends Controller {
  static targets = ['panel', 'card', 'front', 'back', 'bubble', 'openIcon', 'closeIcon', 'form', 'submit', 'error', 'sent']
  static values = {
    url: String,
  }

  connect = () => {
    this.recordPage()
  }

  toggle = () => {
    this.isOpen ? this.close() : this.open()
  }

  open = () => {
    this.panelTarget.classList.remove('hidden')
    this.setBubble(true)
  }

  close = () => {
    if (!this.isOpen) return

    this.panelTarget.classList.add('hidden')
    this.setBubble(false)

    // start fresh next time once a message has gone out
    if (this.isSent) this.reset()
  }

  flip = () => {
    this.cardTarget.classList.add('rotate-y-180')
    this.frontTarget.inert = true
    this.backTarget.inert = false
    this.focusFirstEmpty()
  }

  unflip = () => {
    this.cardTarget.classList.remove('rotate-y-180')
    this.frontTarget.inert = false
    this.backTarget.inert = true
  }

  submit = async (event) => {
    event.preventDefault()
    this.hideError()
    this.submitTarget.disabled = true

    const body = {
      ...Object.fromEntries(new FormData(this.formTarget)),
      page_url: scrubUrl(window.location.href),
      referrer: scrubUrl(document.referrer),
      pages: this.history.slice(0, -1),
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      screen: `${window.screen.width}x${window.screen.height} (window ${window.innerWidth}x${window.innerHeight})`,
    }

    try {
      const response = await post(this.urlValue, { body, responseKind: 'json' })
      const json = await response.json

      if (response.ok) {
        this.showSent()
      }
      else {
        this.showError(json.error)
      }
    }
    catch {
      this.showError()
    }
    finally {
      this.submitTarget.disabled = false
    }
  }

  reset = () => {
    this.formTarget.reset()
    this.formTarget.classList.remove('hidden')
    this.sentTarget.classList.add('hidden')
    this.sentTarget.classList.remove('flex')
    this.hideError()
    this.unflip()
  }

  showSent = () => {
    this.formTarget.classList.add('hidden')
    this.sentTarget.classList.remove('hidden')
    this.sentTarget.classList.add('flex')
  }

  showError = (message = 'Something went wrong, please try again.') => {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  hideError = () => {
    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('hidden')
  }

  setBubble = (open) => {
    this.bubbleTarget.setAttribute('aria-expanded', open)
    this.openIconTarget.classList.toggle('hidden', open)
    this.closeIconTarget.classList.toggle('hidden', !open)
  }

  focusFirstEmpty = () => {
    const fields = [...this.formTarget.querySelectorAll('input:not([tabindex="-1"]), textarea')]
    const field = fields.find(f => !f.value) || fields.at(-1)
    // wait for the flip so the browser doesn't scroll the half-turned card
    setTimeout(() => field?.focus({ preventScroll: true }), 300)
  }

  recordPage = () => {
    const history = this.history
    const url = scrubUrl(window.location.href)

    // turbo previews and reloads connect again for the same page
    if (history.at(-1)?.url === url) return

    history.push({ url, title: document.title, at: new Date().toISOString() })
    this.history = history.slice(-HISTORY_LIMIT)
  }

  get history() {
    try {
      const cutoff = Date.now() - HISTORY_MAX_AGE
      const history = JSON.parse(localStorage.getItem(HISTORY_KEY)) || []
      // scrub again so anything saved before scrubbing existed is cleaned too
      return history
        .filter(page => Date.parse(page.at) > cutoff)
        .map(page => ({ ...page, url: scrubUrl(page.url) }))
        .filter(page => page.url)
    }
    catch {
      return []
    }
  }

  set history(history) {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history))
    }
    catch {
      // storage full or blocked, history is a nice to have
    }
  }

  get isOpen() {
    return !this.panelTarget.classList.contains('hidden')
  }

  get isSent() {
    return !this.sentTarget.classList.contains('hidden')
  }
}
